"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTopic = void 0;
const react_1 = require("react");
/**
 * Async load data from backend using [push-rpc](https://github.com/vasyas/push-rpc).
 *
 * ```typescript
 * const {data} = useTopic(services.client, {
 *   pk: session.user.clientPk
 * })
 * ```
 *
 * In this example `useTopic` will subscribe to topic `services.client` on mount and
 * will unsubscribe from it on unmount (much like useEffect works).
 *
 * If `params` are falsy, no subscription will be performed. This is useful to skip subscription until "parent" data is loaded.
 *
 * `useTopic` will re-subscribe topic on changing topic parameters. In example above, when `session.user.clientPk`
 * change topic will be resubscribed. When re-subscribing, `useTopic` will convert params to JSON strings, so it is ok
 * to use inline objects as params.
 *
 * @typeParam Data Data type that topic should return
 * @typeParam Params Type of parameters that topic should accept
 *
 * @param topic  Push-RPC Topic to load data
 * @param params  params to send to topic
 * @param def  default value to use until data is loaded, undefined by default
 */
function useTopic(topic, params, def, opts) {
    const { resetOnChange, ...callOpts } = opts || {};
    const [data, setData] = (0, react_1.useState)(def); // intentionally breaking type-safety here for convenience
    const [loading, setLoading] = (0, react_1.useState)(true);
    const receiveData = (0, react_1.useCallback)((data) => {
        setLoading(false);
        setData(data);
    }, []);
    (0, react_1.useEffect)(() => {
        if (params) {
            setLoading(true);
            topic.subscribe(receiveData, params, undefined, callOpts);
        }
        return () => {
            if (params) {
                topic.unsubscribe(params, receiveData);
            }
            if (resetOnChange) {
                setData(def);
            }
        };
    }, [JSON.stringify(params)]);
    return { data, loading };
}
exports.useTopic = useTopic;
